import styles from './AssistedModeTablesRelationship.module.css';

import { Field, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';

import Dialog from 'components/ui/Dialog';
import useTranslation from 'components/hooks/useTranslation';
import UiBlocker from 'components/bng/ui/UiBlocker';
import BngForm from 'components/bng/form/BngForm';
import { DefaultDialogActions } from 'components/ui/FormUtils';
import BngSelectSearch from 'components/bng/form/BngSelectSearch';
import BngField from 'components/bng/form/BngField';
import BngCheckbox from 'components/bng/form/BngCheckbox';
import Icon from 'components/ui/common/Icon';
import { findFieldIcon } from 'components/ui/common/DatabaseERModel';
import UUID from 'bng/utils/UUID';
import useReduxDispatch from 'components/hooks/useReduxDispatch';
import { MODALS } from 'components/ui/redux/Actions';
import AssistedModeJoinDialog, {
  buildJoinTypeOptions,
} from 'components/bng/pages/admin/structures/bimQuery/dialogs/AssistedModeJoinDialog';

const buildFieldsTableColumns = ({ t, tableName, fields, selectedFields, setSelectedFields }) => {
  const allSelected = fields.length > 0 && selectedFields.length === fields.length;

  return [
    {
      key: 'fieldCheckbox',
      label: (
        <BngCheckbox
          field={{
            onChange: () => {
              setSelectedFields(allSelected ? [] : fields.map((f) => f.uniqueId));
            },
            value: allSelected,
          }}
          size="md"
        />
      ),
      size: 40,
      render: (row) => {
        const isSelected = selectedFields.includes(row.uniqueId);
        return (
          <BngCheckbox
            field={{
              onChange: () =>
                setSelectedFields((prev) => {
                  if (prev.includes(row.uniqueId)) {
                    return prev.filter((f) => f !== row.uniqueId);
                  } else {
                    return [...prev, row.uniqueId];
                  }
                }),
              value: isSelected,
            }}
            size="md"
          />
        );
      },
    },
    {
      key: 'fieldName',
      label: tableName,
      size: 250,
      render: (row) => (
        <div className={styles.iconFieldLabel}>
          <Icon icon={findFieldIcon(row.type)} />
          {row.name}
        </div>
      ),
    },
    {
      key: 'queryColumns',
      label: t('query.columns'),
      size: 250,
      render: (row) => <span>{row.refColumn}</span>,
    },
  ];
};

function useRelatedFields(newNode, nodes) {
  const tableName = newNode?.data?.tableInfo?.table;
  const newFields = newNode?.data?.tableInfo?.fields || [];
  const otherNodes = nodes.filter((n) => n.id !== newNode.id);

  const findFieldsInOtherNodes = (cond) => otherNodes.flatMap((n) => n.data?.tableInfo?.fields || []).filter(cond);

  return useMemo(() => {
    const related = [];

    newFields.forEach((nf) => {
      const refColumns = [];

      if (nf.key) {
        const { type: keyType, table: refTable, name: refName } = nf.key;

        if (keyType === 'FOREIGN') {
          const pkFields = findFieldsInOtherNodes(
            (f) => f.key?.type === 'PRIMARY' && f.name === refName && f.key.table === refTable
          );

          pkFields.forEach(() => {
            refColumns.push(`${refTable}.${refName}`);
          });
        }

        if (keyType === 'PRIMARY') {
          const fkFields = findFieldsInOtherNodes(
            (f) => f.key?.type === 'FOREIGN' && f.key.table === tableName && f.key.name === nf.name
          );

          fkFields.forEach((fkField) => {
            const fkTable = otherNodes.find((n) => (n.data?.tableInfo?.fields || []).includes(fkField))?.data?.tableInfo
              ?.table;

            if (fkTable) {
              refColumns.push(`${fkTable}.${fkField.name}`);
            }
          });
        }
      }

      const sameNameFields = findFieldsInOtherNodes((f) => f.name === nf.name);

      sameNameFields.forEach((sameNameField) => {
        const sameTable = otherNodes.find((n) => (n.data?.tableInfo?.fields || []).includes(sameNameField))?.data
          ?.tableInfo?.table;

        if (sameTable) {
          const refColumn = `${sameTable}.${nf.name}`;
          if (!refColumns.includes(refColumn)) {
            refColumns.push(refColumn);
          }
        }
      });

      if (refColumns.length > 0) {
        related.push({ ...nf, refColumns });
      }
    });

    return related;
  }, [newFields, nodes, tableName]);
}

export default function AssistedModeTablesRelationship({
  closeModal,
  joins,
  newNode,
  nodes = [],
  onChange = _.noop,
  onDeleteTable,
}) {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const [selectedFields, setSelectedFields] = useState([]);

  const tableName = newNode.data?.tableInfo?.table;
  const relatedFields = useRelatedFields(newNode, nodes);

  const expandedFields = useMemo(() => {
    return relatedFields.flatMap((field) => {
      if (field.refColumns && field.refColumns.length > 1) {
        return field.refColumns.map((refColumn) => ({
          ...field,
          refColumn,
          uniqueId: `${field.name}-${refColumn}`,
        }));
      } else if (field.refColumns && field.refColumns.length === 1) {
        return [
          {
            ...field,
            refColumn: field.refColumns[0],
            uniqueId: `${field.name}-${field.refColumns[0]}`,
          },
        ];
      } else {
        return [
          {
            ...field,
            uniqueId: `${field.name}-no-ref`,
          },
        ];
      }
    });
  }, [relatedFields]);

  const displayedFields = useMemo(() => {
    return [...expandedFields].sort((a, b) => {
      const aSel = selectedFields.includes(a.uniqueId);
      const bSel = selectedFields.includes(b.uniqueId);
      if (aSel === bSel) return 0;
      return aSel ? -1 : 1;
    });
  }, [expandedFields, selectedFields]);

  // select by default FK fields or PK fields with a corresponding FK
  useEffect(() => {
    const defaultSelected = expandedFields
      .filter((f) => {
        if (!f.key) return false;

        if (f.key.type === 'FOREIGN') return true;

        if (f.key.type === 'PRIMARY') {
          const [targetTable, targetField] = (f.refColumn || '').split('.');
          if (!targetTable || !targetField) return false;

          const targetNode = nodes.find((n) => n.data?.tableInfo?.table === targetTable);
          if (!targetNode) return false;

          const fkField = (targetNode.data?.tableInfo?.fields || []).find((ff) => ff.name === targetField);

          return fkField?.key?.type === 'FOREIGN' && fkField.key.table === tableName && fkField.key.name === f.name;
        }

        return false;
      })
      .map((f) => f.uniqueId);

    setSelectedFields(defaultSelected);
  }, [expandedFields, nodes, tableName]);

  const joinTypeOpts = useMemo(() => buildJoinTypeOptions(t), []);

  const fieldsTableColumns = buildFieldsTableColumns({
    t,
    tableName,
    fields: expandedFields,
    selectedFields,
    setSelectedFields,
  });

  return (
    <Dialog
      className={`${styles.dialogContainer}`}
      title={`${t('relationship.between.tables')}`}
      onClose={closeModal}
      hideClose={true}
      newDialogLayout={true}
    >
      <UiBlocker>
        <Formik
          initialValues={{
            joinType: 'INNER_JOIN',
          }}
          onSubmit={(values) => {
            if (selectedFields.length > 0) {
              const newJoins = [];
              const updatedNodes = nodes.map((node) => {
                if (node.id === newNode.id) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      tableInfo: {
                        ...node.data.tableInfo,
                        joinType: values.joinType,
                      },
                    },
                  };
                }
                return node;
              });

              selectedFields.forEach((uniqueId) => {
                const expandedField = expandedFields.find((f) => f.uniqueId === uniqueId);
                if (!expandedField?.refColumn) {
                  return;
                }

                const [targetTable, targetField] = expandedField.refColumn.split('.');
                if (!(targetTable && targetField)) {
                  return;
                }

                const targetNode = nodes.find((n) => n.data?.tableInfo?.table === targetTable);

                const tableOrder =
                  newNode.data.tableInfo.tableOrder > targetNode.data.tableInfo.tableOrder
                    ? newNode.data.tableInfo.tableOrder
                    : targetNode.data.tableInfo.tableOrder;

                if (targetNode) {
                  newJoins.push({
                    id: UUID.generate(),
                    sourceNodeId: newNode.id,
                    sourceNodeName: newNode.data.tableInfo.table,
                    sourceField: expandedField.name,
                    targetNodeId: targetNode.id,
                    targetNodeName: targetNode.data.tableInfo.table,
                    targetField: targetField,
                    joinType: values.joinType,
                    operator: '=',
                    logicalOperator: newJoins.length === 0 ? null : 'AND',
                    tableOrder: tableOrder,
                  });
                }
              });

              const updatedJoins = [...joins, ...newJoins];
              onChange(updatedNodes, updatedJoins);
              closeModal(true);
            } else {
              closeModal(true);
              dispatch(
                MODALS.open(AssistedModeJoinDialog, {
                  nodeId: newNode.id,
                  nodes: nodes || [],
                  joins: joins || [],
                  onChange: ({ nodes, joins }) => {
                    onChange(nodes, joins);
                  },
                  onDeleteTable: onDeleteTable,
                })
              );
            }
          }}
        >
          {({ submitCount, errors }) => {
            return (
              <BngForm>
                <Dialog.Body>
                  <div className={styles.tableJoinWrapper}>
                    <div className={styles.tableInfo}>
                      <span>{t('table')}</span>
                      <span>{tableName}</span>
                    </div>
                    <div className={styles.joinType}>
                      <Field
                        name={`joinType`}
                        className={styles.joinTypeSelect}
                        component={BngField}
                        label={t('type')}
                        inputComponent={BngSelectSearch}
                        showSearchBar={false}
                        clearable={false}
                        options={joinTypeOpts}
                      />
                    </div>
                  </div>
                  {displayedFields.length > 0 ? (
                    <>
                      <div className={styles.tablesRelationshipsLabel}>
                        <span>{t('relationships.between.tables.found')}</span>
                      </div>
                      <div className={styles.fieldsTableContainer}>
                        <div className={styles.fieldsTableHeader}>
                          {fieldsTableColumns.map((col) => (
                            <div key={col.key} style={{ width: col.size }}>
                              {col.label}
                            </div>
                          ))}
                        </div>
                        <div className={styles.fieldsTableBody}>
                          {displayedFields.map((field) => (
                            <div className={styles.fieldsTableRow} key={field.uniqueId}>
                              {fieldsTableColumns.map((col) => (
                                <div key={col.key} style={{ width: col.size }}>
                                  {col.render(field)}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className={styles.noFieldsRow}>{t('relationships.between.tables.not.found')}</div>
                  )}
                </Dialog.Body>

                <Dialog.Footer>
                  <DefaultDialogActions
                    closeModal={closeModal}
                    submitCount={submitCount}
                    errors={errors}
                    okLabel="apply"
                    cancelLabel="cancel"
                  />
                </Dialog.Footer>
              </BngForm>
            );
          }}
        </Formik>
      </UiBlocker>
    </Dialog>
  );
}
